import { useMemo, useRef, useState } from "react";
import type { AllowedValuesDTO } from "api/allowed-values";
import type { TargetLevel } from "../types";
import { ENTITY_ITEMS_PER_PAGE } from "../consts";

export interface EntityOption {
  label: string;
  value: string;
}

function getEntityOptions(
  targetLevel: TargetLevel | "",
  allowedValues: AllowedValuesDTO | undefined
): EntityOption[] {
  if (!targetLevel || targetLevel === "Corporate") return [];
  if (targetLevel === "Business Unit" && allowedValues?.businessUnits?.length) {
    return allowedValues.businessUnits.map((name) => ({ label: name, value: name }));
  }
  if (targetLevel === "Site" && allowedValues?.sites?.length) {
    return allowedValues.sites.map((s) => ({ label: s.siteName, value: s.siteName }));
  }
  return [];
}

function containsText(label: string, search: string): boolean {
  return typeof label === "string" && label.toLowerCase().indexOf(search.toLowerCase()) > -1;
}

export function useEntitySelect(
  targetLevel: TargetLevel | "",
  allowedValues: AllowedValuesDTO | undefined
) {
  const [searchText, setSearchText] = useState("");
  const [visibleCount, setVisibleCount] = useState(ENTITY_ITEMS_PER_PAGE);
  const [menuOpen, setMenuOpen] = useState(false);
  const [selectWidth, setSelectWidth] = useState<number | null>(null);
  const selectRef = useRef<HTMLDivElement>(null);

  const entityOptions = useMemo(
    () => getEntityOptions(targetLevel, allowedValues),
    [targetLevel, allowedValues?.businessUnits, allowedValues?.sites] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const filteredOptions = useMemo(
    () => entityOptions.filter((opt) => containsText(opt.label, searchText)),
    [entityOptions, searchText]
  );

  const displayedOptions = useMemo(
    () => filteredOptions.slice(0, visibleCount),
    [filteredOptions, visibleCount]
  );

  const hasMoreItems = filteredOptions.length > visibleCount;
  const remainingCount = filteredOptions.length - visibleCount;

  const handleSearchChange = (value: string) => {
    setSearchText(value);
    setVisibleCount(ENTITY_ITEMS_PER_PAGE);
  };

  const handleLoadMore = () => {
    setVisibleCount((prev) => prev + ENTITY_ITEMS_PER_PAGE);
  };

  const handleMenuClose = () => {
    setSearchText("");
    setVisibleCount(ENTITY_ITEMS_PER_PAGE);
    setMenuOpen(false);
  };

  const handleMenuOpen = () => {
    const w = selectRef.current?.offsetWidth ?? null;
    setSelectWidth(w);
    setMenuOpen(true);
  };

  const reset = () => {
    setSearchText("");
    setVisibleCount(ENTITY_ITEMS_PER_PAGE);
  };

  return {
    entityOptions,
    displayedOptions,
    hasMoreItems,
    remainingCount,
    searchText,
    handleSearchChange,
    handleLoadMore,
    handleMenuClose,
    handleMenuOpen,
    selectRef,
    menuOpen,
    selectWidth,
    reset,
  };
}

export type EntitySelectState = ReturnType<typeof useEntitySelect>;
